import { noSeq, outsideTree, srceNames, srceSeqIds } from "../../utils/utils";

import Col from "react-bootstrap/Col";
import MissingSrces from "./MissingSrces";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import TreeChart from "./TreeChart";
import TreeLegend from "./TreeLegend";
import TreeMenu from "./TreeMenu";
import { parseNewick } from "../../utils/parsers";
import { useSelector } from "react-redux";
import { useSurveyMode } from "../../contexts/SurveyModeProvider";
import { useMemo } from "react";

const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

export default function Tree(micro_id) {
  const newickTree = useSelector((state) => state.patient.tree.tree);
  const ptObj = useSelector((state) => state.patient.patient);
  const ptId = useSelector((state) => state.patient.micro_id);
  const nameMap = useSelector((state) => state.patient.tree.name_map);
  const overlap_level = useSelector((state) => state.patient.patient.overlap_level);
  const distance_matrix = useSelector(
    (state) => state.patient.tree.distance_matrix
  );
  const graphNodes = useSelector((state) => state.patient.graph.nodes || []);
  const graphConstraints = useSelector(
    (state) => state.patient.graph.constrain || []
  );
  const graphPruned = useSelector(
    (state) => state.patient.graph.pruneForPhylos
  );
  const overlapConstraint = useSelector(
    (state) => state.patient.tree.overlap_constraint
  );
  const overlapConstraintSeqIds = useSelector(
    (state) => state.patient.patient.constrain || []
  );
  const overlapView = useSelector(
    (state) => state.patient.overlapView || "overlaps"
  );
  const survey = useSelector((state) => state.survey);
  const activeCase = useSelector((state) => state.survey.activeCase);
  const debugSurveyMode = useSelector(
    (state) => state.survey.debug_survey_mode
  );

  const { debug_mode } = useSurveyMode();

  const ptSeqId = ptObj?.pt?.seq_id;

  const snpDistances =
    ptSeqId && distance_matrix && distance_matrix[ptSeqId] !== undefined
      ? distance_matrix[ptSeqId]
      : undefined;

  const showFutureSequences = useSelector(state => state.patient.tree.show_future_sequences);

  const survey_mode =
    survey.surveyHistory[activeCase?.micro_id]?.mode || 0;
  const geneticHighlightEnabled =
    survey_mode >= 3 || debugSurveyMode >= 3;

  const graphHighlightSeqIds = useMemo(() => {
    const seqIds = new Set();
    const nodeIdSet = graphPruned
      ? new Set((graphConstraints || []).map((id) => String(id)))
      : null;

    (graphNodes || []).forEach((node) => {
      if (nodeIdSet && !nodeIdSet.has(String(node?.id))) return;
      if (
        node?.seq_id !== null &&
        node?.seq_id !== undefined &&
        node?.seq_id !== ""
      ) {
        seqIds.add(String(node.seq_id));
      }
    });

    return Array.from(seqIds);
  }, [graphNodes, graphConstraints, graphPruned]);

  const graphHighlightNames = useMemo(() => {
    const names = new Set();
    (graphHighlightSeqIds || []).forEach((seqId) => {
      const entry = nameMap?.[seqId];
      if (Array.isArray(entry) && entry[0]) names.add(entry[0]);
    });
    return Array.from(names);
  }, [graphHighlightSeqIds, nameMap]);

  const defaultHighlightNames = useMemo(
    () => (ptObj ? srceNames(ptObj).map((item) => item.name) : []),
    [ptObj]
  );

  const defaultHighlightSeqIds = useMemo(
    () => (ptObj ? srceSeqIds(ptObj) : []),
    [ptObj]
  );

  const namesForTree = useMemo(() => {
    const showingGraph = overlapView === "graph";
    if (
      showingGraph &&
      geneticHighlightEnabled &&
      graphHighlightNames.length
    ) {
      return graphHighlightNames;
    }
    return defaultHighlightNames;
  }, [
    overlapView,
    geneticHighlightEnabled,
    graphHighlightNames,
    defaultHighlightNames,
  ]);

  const seqIdsForTree = useMemo(() => {
    const showingGraph = overlapView === "graph";
    if (
      showingGraph &&
      geneticHighlightEnabled &&
      graphHighlightSeqIds.length
    ) {
      return graphHighlightSeqIds;
    }
    return defaultHighlightSeqIds;
  }, [
    overlapView,
    geneticHighlightEnabled,
    graphHighlightSeqIds,
    defaultHighlightSeqIds,
  ]);

  const pruneSeqIds = useMemo(() => {
    if (!overlapConstraint) return [];
    const showingGraph = overlapView === "graph";
    let baseSeqIds = [];
    if (showingGraph && graphHighlightSeqIds.length) {
      baseSeqIds = graphHighlightSeqIds;
    } else if (
      Array.isArray(overlapConstraintSeqIds) &&
      overlapConstraintSeqIds.length
    ) {
      baseSeqIds = overlapConstraintSeqIds;
    }
    if (!baseSeqIds.length) {
      return [];
    }

    const seqIdSet = new Set(
      (baseSeqIds || []).map((id) => String(id))
    );
    if (ptSeqId !== null && ptSeqId !== undefined && ptSeqId !== "") {
      seqIdSet.add(String(ptSeqId));
    }
    return Array.from(seqIdSet);
  }, [
    overlapConstraint,
    overlapView,
    graphHighlightSeqIds,
    overlapConstraintSeqIds,
    ptSeqId,
  ]);

  const parsedTree = useMemo(() => {
    if (!newickTree) return null;
    if (typeof newickTree === "string") {
      return parseNewick(newickTree, nameMap);
    }
    if (newickTree && typeof newickTree.tree === "string") {
      return parseNewick(newickTree.tree, nameMap);
    }
    return null;
  }, [newickTree, nameMap]);

  return (
    <>
      {
        // First, check if we're still loading data or if there's a mismatch in patient IDs
        newickTree === undefined ||
        ptObj.pt === undefined ||
        ptId !== micro_id.micro_id ? (
          <Spinner animation="border" />
        ) : newickTree.tree === null ? (
          // If we have the newickTree but its tree property is null, show a specific message
          <Row>
            <Col>
              <p>This patient has no pathogen sequencing data.</p>
            </Col>
          </Row>
        ) : (
          // Once all checks are passed, render the component's main content
          <>
            <div className="button-container">
              <div className="button-container" style={{ width: "100%" }}>
                <TreeMenu
                  seqIds={srceSeqIds(ptObj)}
                  indexSeqId={ptObj.pt.seq_id}
                  noSeq={noSeq(ptObj, nameMap)}
                  debug_mode={debug_mode}
                />
              </div>
            </div>
            <Row></Row>
            {parsedTree ? (
              <TreeChart
                jsonTree={parsedTree}
                primary={ptObj.pt.name}
                firstPos={ptObj.pt.first_positive}
                names={namesForTree}
                seqIds={seqIdsForTree}
                pruneSeqIds={pruneSeqIds}
                constrainToSeqIds={overlapConstraint}
                noSeq={noSeq(ptObj, nameMap)}
                snpDistances={snpDistances}
                showFutureSequences={showFutureSequences}
              />
            ) : (
              <Spinner animation="border" />
            )}
            <Row></Row>
            <Row>
              <TreeLegend pt_name={ptObj.pt.name} overlap_level={overlap_level}/>
            </Row>
            <Row></Row>
            <Row>
              <MissingSrces ptObj={ptObj} ></MissingSrces>
            </Row>
          </>
        )
      }
    </>
  );
}
